import { Button, ButtonGroup, Stack, Typography } from '@mui/material'
import { lighten } from '@mui/system'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  AWSAuthenticationType,
  AWSRoleCreationMethod,
  getAWSAuthenticationTypes,
  getAWSRoleCreationMethods
} from 'components/status-checks/authentication-form-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  useObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'
import { isPrivateEdition } from 'utils/app-config'

export const AWSAuthenticationTypeSelect: React.FC = () => {
  const { trans, language } = useTranslation()
  const { control, formState, getValues, reset, setValue } =
    useFormContext<StatusCheckFormValues>()
  const [awsAuthenticationType, awsRoleCreationMethod] =
    useWatch<StatusCheckFormValues>({
      control,
      name: ['awsAuthenticationType', 'awsRoleCreationMethod']
    })

  const { setAuthenticationResponse } =
    useObservabilityToolAuthenticationContext()

  const handleSelectAWSAuthenticationType = (type: AWSAuthenticationType) => {
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
    setValue('awsAuthenticationType', type)

    if (isPrivateEdition()) {
      // PE only supports the Manual IAM-role creation flow (no CloudFormation).
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.Manual)
    } else if (!awsRoleCreationMethod) {
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.CloudFormation)
    }

    // Necessary to preserve the dirty state of the form when switching between authentication types
    if (formState.isDirty) {
      reset(getValues(), { keepValues: true })
    }
  }

  const handleSelectAWSRoleCreationMethod = (method: AWSRoleCreationMethod) => {
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
    setValue('awsRoleCreationMethod', method)
  }

  const awsAuthenticationTypes = getAWSAuthenticationTypes(language)
  const awsRoleCreationMethods = getAWSRoleCreationMethods()

  return (
    <Stack mb={2} spacing={2}>
      <Typography>
        {trans(
          'status_checks.authentication.aws.authentication_type.select.prompt'
        )}
      </Typography>
      <ButtonGroup
        aria-label={trans(
          'status_checks.authentication.aws.authentication_type.select.aria.label'
        )}
        data-testid="aws-authentication-type-select-button-group"
        variant="outlined"
      >
        {awsAuthenticationTypes.map((type) => (
          <Button
            key={type.value}
            data-testid={`aws-authentication-type-${type.value}`}
            sx={(muiTheme) => ({
              backgroundColor:
                awsAuthenticationType === type.value &&
                lighten(muiTheme.palette.primary.light, 0.9)
            })}
            onClick={(e) => {
              e.preventDefault()
              handleSelectAWSAuthenticationType(type.value)
            }}
          >
            {type.label}
          </Button>
        ))}
      </ButtonGroup>
      {awsAuthenticationType === AWSAuthenticationType.IAMRole && (
        <Stack spacing={2}>
          <Typography>
            {trans(
              'status_checks.authentication.aws.role_creation_method.select.prompt'
            )}
          </Typography>
          <ButtonGroup
            aria-label={trans(
              'status_checks.authentication.aws.role_creation_method.select.aria.label'
            )}
            variant="outlined"
          >
            {awsRoleCreationMethods.map((method) => (
              <Button
                key={method.value}
                data-testid={`aws-role-creation-method-${method.value}`}
                sx={(muiTheme) => ({
                  backgroundColor:
                    awsRoleCreationMethod === method.value &&
                    lighten(muiTheme.palette.primary.light, 0.9)
                })}
                onClick={(e) => {
                  e.preventDefault()
                  handleSelectAWSRoleCreationMethod(method.value)
                }}
              >
                {method.label}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>
      )}
    </Stack>
  )
}
